import React from 'react'
import { cn } from '../../utils/cn'
import { Tooltip } from './tooltip'

export interface EnvironmentSwitchProps {
  /** Current environment. */
  environment: 'sandbox' | 'live'
  /**
   * Called when the user picks the *other* environment. The consumer decides
   * what happens next (e.g. open a confirmation dialog or a go-live flow) — this
   * primitive is uncontrolled beyond reflecting `environment`.
   */
  onToggle: () => void
  isDisabled?: boolean
  /**
   * Show full text labels. When false, renders the compact variant (stacked,
   * shorter) for collapsed sidebars.
   */
  showLabel?: boolean
  /** Tooltip shown when the switch is disabled. */
  tooltip?: string
  className?: string
  id?: string
}

const SEGMENTS: { value: 'sandbox' | 'live'; label: string }[] = [
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'live', label: 'Live' },
]

/**
 * Segmented Sandbox / Live environment selector. Both options are always
 * visible with the active one highlighted, so it communicates the current
 * environment *and* the available action at once. Purely presentational: it
 * delegates the switch decision to `onToggle`.
 */
export const EnvironmentSwitch: React.FC<EnvironmentSwitchProps> = ({
  environment,
  onToggle,
  isDisabled = false,
  showLabel = true,
  tooltip,
  className,
  id = 'environment-switch',
}) => {
  const compact = !showLabel

  const control = (
    <div
      role="radiogroup"
      aria-label="Environment"
      id={id}
      className={cn(
        // Same segmented-pill chrome in both sizes so it always reads as a
        // toggle. Compact (collapsed rail) just shrinks to single-letter S / L
        // segments with the full name on hover.
        'inline-flex flex-row items-center gap-0.5 rounded-full border border-line-subtle bg-surface-muted p-0.5',
        isDisabled && 'opacity-60',
        className,
      )}
    >
      {SEGMENTS.map(({ value, label }) => {
        const isActive = value === environment
        // Only the *inactive* segment is actionable, and only when enabled.
        const isSegmentDisabled = isDisabled && !isActive
        const segment = (
          <button
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={label}
            disabled={isSegmentDisabled}
            onClick={() => {
              if (isActive || isSegmentDisabled) return
              onToggle()
            }}
            className={cn(
              'flex items-center justify-center gap-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
              compact ? 'h-6 w-6 rounded-full text-[0.6875rem]' : 'rounded-full px-3 py-1 text-xs',
              isActive
                ? 'bg-surface-section font-semibold text-content-primary shadow-sm'
                : isSegmentDisabled
                  ? 'cursor-not-allowed font-medium text-content-muted/50'
                  : 'font-medium text-content-muted hover:text-content-secondary',
            )}
          >
            {!compact && (
              <span
                aria-hidden
                className={cn(
                  'h-1.5 w-1.5 rounded-full transition-colors',
                  isActive ? 'bg-success' : 'bg-current opacity-40',
                )}
              />
            )}
            {compact ? label.charAt(0) : label}
          </button>
        )

        // In the collapsed rail the label is reduced to a single letter, so the
        // full environment name is surfaced on hover.
        return compact ? (
          <Tooltip key={value} label={label} placement="right" hasArrow>
            {segment}
          </Tooltip>
        ) : (
          <React.Fragment key={value}>{segment}</React.Fragment>
        )
      })}
    </div>
  )

  if (isDisabled && tooltip) {
    return (
      <Tooltip label={tooltip} placement="right" hasArrow>
        <span className={cn(compact && 'inline-flex')}>{control}</span>
      </Tooltip>
    )
  }

  return control
}

EnvironmentSwitch.displayName = 'EnvironmentSwitch'
